import React from 'react';
import Link from 'next/link';
import { AlertCircle, ArrowLeft } from 'lucide-react';

type RemovedVariant = 'profile' | 'listing' | 'thread' | 'resource' | 'event' | 'newsletter' | 'service' | 'generic';

interface RemovedContentProps {
  variant?: RemovedVariant;
  title?: string;
  description?: string;
  backHref?: string;
  backLabel?: string;
}

const VARIANT_DEFAULTS: Record<RemovedVariant, { title: string; description: string }> = {
  profile: {
    title: 'Profile unavailable',
    description: 'This resident profile is no longer available. It may have been removed or the account may be inactive.',
  },
  listing: {
    title: 'Listing unavailable',
    description: 'This marketplace listing is no longer available. It may have been sold, removed, or expired.',
  },
  thread: {
    title: 'Thread unavailable',
    description: 'This conversation thread is no longer available. It may have been removed by the author or a moderator.',
  },
  resource: {
    title: 'Resource unavailable',
    description: 'This resource is no longer available. It may have been updated or removed.',
  },
  event: {
    title: 'Event unavailable',
    description: 'This event is no longer available. It may have been cancelled or removed.',
  },
  newsletter: {
    title: 'Issue unavailable',
    description: 'This newsletter issue is no longer available.',
  },
  service: {
    title: 'Service unavailable',
    description: 'This service listing is no longer available. The provider may have updated or removed it.',
  },
  generic: {
    title: 'Content unavailable',
    description: 'This content is no longer available.',
  },
};

export default function RemovedContent({
  variant = 'generic',
  title,
  description,
  backHref,
  backLabel = 'Go back',
}: RemovedContentProps) {
  const defaults = VARIANT_DEFAULTS[variant];

  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
        style={{
          background: 'rgba(167,163,196,0.06)',
          border: '1px solid rgba(167,163,196,0.15)',
        }}
      >
        <AlertCircle size={24} style={{ color: 'rgba(167,163,196,0.60)' }} />
      </div>

      <h2 className="font-display font-semibold text-[20px] mb-2.5" style={{ color: '#F2F4FF' }}>
        {title ?? defaults.title}
      </h2>
      <p className="text-[14px] leading-relaxed max-w-sm mb-7" style={{ color: '#A7A3C4' }}>
        {description ?? defaults.description}
      </p>

      {backHref && (
        <Link
          href={backHref}
          className="inline-flex items-center gap-2 btn-secondary px-5 py-2.5 text-[13.5px]"
        >
          <ArrowLeft size={14} />
          {backLabel}
        </Link>
      )}
    </div>
  );
}
